// DeskSettingsPage — Administration -> Desk Settings (managing broker saves):
// phone lines (number, label, branch, purpose — inbound texts take their branch from the
// line, and repair texts to a customer-service line become maintenance requests), phone
// menu notes (the PBX remains the source), and the per-state deal milestone templates.
import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser } from '../../hooks/useCurrentUser';
import PageHeader, { AboutThisPage } from '../../components/shared/PageHeader';

const BASIS = { business: 'business days', calendar: 'calendar days', calendar_roll: 'calendar, rolls past weekend/holiday' };

export default function DeskSettingsPage() {
    const api = useApiClient();
    const user = getCurrentUser();
    const canSave = !!user?.isManagingBroker;
    const [s, setS] = useState(null);
    const [lines, setLines] = useState([]);
    const [menu, setMenu] = useState('');
    const [notice, setNotice] = useState('');
    const [tpl, setTpl] = useState({});
    const [state, setState] = useState('IL');
    const [msg, setMsg] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get('/api/desk/settings');
            setS(r); setLines(r.phone_lines || []); setMenu(r.phone_menu || ''); setNotice(r.agency_notice || '');
            const t = {};
            for (const code of ['IL', 'FL']) t[code] = (r.milestone_templates[code] && r.milestone_templates[code].items) || r.defaults.milestone_templates[code].items;
            setTpl(t);
        } catch (e) { setMsg({ bad: true, text: e.message }); }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const save = async (body, what) => {
        try { await api.put('/api/desk/settings', body); setMsg({ text: `${what} saved.` }); load(); } catch (e) { setMsg({ bad: true, text: e.message }); }
    };
    const setLine = (i, k, v) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
    const setItem = (i, k, v) => setTpl({ ...tpl, [state]: tpl[state].map((m, j) => (j === i ? { ...m, [k]: k === 'days' ? Number(v) : v } : m)) });

    if (!s) return <div className="p-6">{msg ? msg.text : 'Loading…'}</div>;
    const box = 'bg-white p-6 rounded-lg shadow-md space-y-3';
    const inp = 'p-2 border rounded text-sm';
    return (
        <div className="space-y-6">
            <div>
                <PageHeader title="Desk Settings" description="Phone lines, the agency notice and the phone menu for the Brokerage Desk." className="!mb-3" />
                <AboutThisPage className="!mb-0">
                    <p>Office hours, chat greeting and consent wording are on Website Chat → Settings; holidays and office closures on Command Center → Bank holidays; the e-mail intake key on Integrations.</p>
                </AboutThisPage>
                {!canSave && <p className="text-amber-700 text-sm mt-1">Only the managing broker can save these.</p>}
                {msg && <p className={`mt-2 text-sm ${msg.bad ? 'text-red-700' : 'text-emerald-700'}`}>{msg.text}</p>}
            </div>

            <section className={box}>
                <h2 className="text-lg font-semibold">Phone lines</h2>
                <p className="text-sm text-slate-500">Texts to a line take its branch. A repair text (leak, no heat…) to a <b>customer_service</b> line becomes a maintenance request even from an unknown number.</p>
                {lines.map((l, i) => (
                    <div key={i} className="flex flex-wrap gap-2 items-center">
                        <input className={inp} placeholder="(773) 555-0100" value={l.number || ''} onChange={(e) => setLine(i, 'number', e.target.value)} />
                        <input className={inp} placeholder="Label" value={l.label || ''} onChange={(e) => setLine(i, 'label', e.target.value)} />
                        <select className={inp} value={l.branch || ''} onChange={(e) => setLine(i, 'branch', e.target.value)}>
                            <option value="">branch…</option>{s.choices.branches.map((b) => <option key={b}>{b}</option>)}</select>
                        <select className={inp} value={l.purpose || 'main'} onChange={(e) => setLine(i, 'purpose', e.target.value)}>
                            {s.choices.purposes.map((p) => <option key={p}>{p}</option>)}</select>
                        <button className="text-red-700 text-sm" onClick={() => setLines(lines.filter((_, j) => j !== i))}>remove</button>
                    </div>
                ))}
                <div className="flex gap-2">
                    <button className="px-3 py-2 text-sm bg-slate-200 rounded" onClick={() => setLines([...lines, { number: '', label: '', branch: '', purpose: 'main' }])}>Add a line</button>
                    {canSave && <button className="px-3 py-2 text-sm bg-slate-800 text-white rounded" onClick={() => save({ phone_lines: lines }, 'Phone lines')}>Save phone lines</button>}
                </div>
            </section>

            <section className={box}>
                <h2 className="text-lg font-semibold">Agency notice on first-response e-mails</h2>
                <p className="text-sm text-slate-500">Added, word for word, to the end of every first-response e-mail draft. Have your attorney approve it. Leave blank to use the standard text shown below.</p>
                <textarea className={`${inp} w-full`} rows={4} value={notice} onChange={(e) => setNotice(e.target.value)} />
                <p className="text-xs text-slate-400">Standard: {Object.values(s.defaults.agency_notice || {}).join(' / ')}</p>
                {canSave && <button className="px-3 py-2 text-sm bg-slate-800 text-white rounded" onClick={() => save({ agency_notice: notice }, 'Agency notice')}>Save agency notice</button>}
            </section>

            <section className={box}>
                <h2 className="text-lg font-semibold">Phone menu (notes)</h2>
                <p className="text-sm text-slate-500">What callers hear and where each option rings. The phone system (PBX) is configured separately; keep this in step with it so staff can see it here.</p>
                <textarea className={`${inp} w-full`} rows={6} value={menu} onChange={(e) => setMenu(e.target.value)} placeholder={'1 — Sales (Chicago)\n2 — Florida office\n3 — Customer service / maintenance'} />
                {canSave && <button className="px-3 py-2 text-sm bg-slate-800 text-white rounded" onClick={() => save({ phone_menu: menu }, 'Phone menu')}>Save phone menu</button>}
            </section>

            <section className={box}>
                <div className="flex flex-wrap justify-between items-center gap-2">
                    <h2 className="text-lg font-semibold">Deal milestone defaults</h2>
                    <select className={inp} value={state} onChange={(e) => setState(e.target.value)}><option value="IL">Illinois</option><option value="FL">Florida</option></select>
                </div>
                <p className="text-sm text-slate-500">{s.defaults.milestone_templates[state].contract}. New deals start from these; every deal can still change its own. Items marked [verify] are figures the contract leaves to the parties.</p>
                <div className="overflow-x-auto">
                    <table className="text-sm w-full">
                        <thead><tr className="text-left text-slate-500"><th className="pr-2">Milestone</th><th className="pr-2">Days</th><th className="pr-2">Day type</th><th>Counts from</th></tr></thead>
                        <tbody>
                            {(tpl[state] || []).map((m, i) => (
                                <tr key={m.mkey}>
                                    <td className="pr-2 py-1"><input className={`${inp} w-full`} value={m.label} onChange={(e) => setItem(i, 'label', e.target.value)} /></td>
                                    <td className="pr-2"><input type="number" className={`${inp} w-20`} value={m.days} onChange={(e) => setItem(i, 'days', e.target.value)} /></td>
                                    <td className="pr-2"><select className={inp} value={m.basis} onChange={(e) => setItem(i, 'basis', e.target.value)}>{Object.entries(BASIS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></td>
                                    <td><select className={inp} value={m.anchor} onChange={(e) => setItem(i, 'anchor', e.target.value)}><option value="contract">contract date</option><option value="closing">closing date</option></select></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {canSave && (
                    <div className="flex gap-2">
                        <button className="px-3 py-2 text-sm bg-slate-800 text-white rounded" onClick={() => save({ milestone_templates: { ...Object.fromEntries(Object.entries(tpl).map(([k, items]) => [k, { items }])) } }, 'Milestone defaults')}>Save milestone defaults</button>
                        <button className="px-3 py-2 text-sm bg-slate-200 rounded" onClick={() => setTpl({ ...tpl, [state]: s.defaults.milestone_templates[state].items })}>Reset {state} to the standard</button>
                    </div>
                )}
            </section>
        </div>
    );
}
